import { IForm } from "./../../store/forms/types";
import { IFieldResponseData } from "./../../store/fields/types";
import { fieldsInitials, formInitials } from "./initials";
import { paymentInitial } from "./../paymentActions";
import { formSettings } from "../../store/settings";

interface MyObject {
    [key: string]: any;
}

/* Common functions for comparing | merging objects  */

export function getObjectWithChangedValues(obj1: MyObject, obj2: MyObject) {
    const result: MyObject = {};

    function compareProperties(obj1: MyObject, obj2: MyObject, currentPath: string = "") {
        for (const prop in obj2) {
            const newPath = currentPath ? `${currentPath}.${prop}` : prop;

            if (obj2 && obj1 && typeof obj2[prop] === "object" && obj1.hasOwnProperty(prop)) {
                // for file types and formats validation
                if (prop === "formats" || prop === "types" || prop === "points") {
                    result[newPath] = obj2[prop];
                } else {
                    compareProperties(obj1[prop], obj2[prop], newPath);
                }
            } else if (obj2 && obj1 && obj1[prop] !== obj2[prop]) {
                result[newPath] = obj2[prop];
            }
        }
    }

    compareProperties(obj1, obj2);
    return result;
}

export function mergeObjects(obj1: MyObject, obj2: MyObject): MyObject {
    const result: MyObject = { ...obj1 };
    for (const key in obj1) {
        if (obj2.hasOwnProperty(key)) {
            if (key === "json") {
                result[key] = obj2[key];
            } else if (
                typeof obj1[key] === "object" &&
                !Object.is(obj1[key], null) &&
                typeof obj2[key] === "object" &&
                !Object.is(obj2[key], null)
            ) {
                if (key === "color") {
                    result[key] = obj2[key];
                    continue;
                }
                if (Array.isArray(obj2[key])) {
                    result[key] = obj2[key];
                } else {
                    result[key] = mergeObjects(obj1[key], obj2[key]);
                }
            } else {
                result[key] = obj2[key];
            }
        }
    }
    return result;
}

/*  Functions for fields data actions  */

export function getFieldsChangedValues(prevState: MyObject, currentState: MyObject) {
    const result: MyObject = getObjectWithChangedValues(prevState, currentState);
    return Object.keys(result).map(element => {
        if(element === 'label.value' || element === 'text' || element === 'html'){
         return{
             [element]: result[element]
            }  
        }
        else{
            return {
                [element]: result[element],
                'label.value': currentState.label.value,
            };
        }
    });
}

export function completeFieldsData(fieldsData: IFieldResponseData[]): any[] {
    const resultData: any[] = [];
    fieldsData.forEach(element => {
        const { _id, type } = element;
        resultData.push({ ...mergeObjects(fieldsInitials[element.type], element), _id, type });
    });
    return resultData;
}

/* Functions for Form data actions  */

export function completeFormStyles(formStyles: IForm["styles"]) {
    const resultData: any = mergeObjects(formInitials.styles, formStyles);
    return resultData;
}

export const completeFormButtonsStyles = (formButtonStyles: IForm["styles"]["buttons"]) => {
    const resultData = mergeObjects(formInitials.styles.buttons, formButtonStyles || {});
    return resultData;
};

export function completeFormSetting(formSettingsData: MyObject): any {
    return mergeObjects(formSettings, formSettingsData || {});
}

export function getChangedFormSettingsValues(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);

    return Object.keys(result).map(element => {
        return {
            ["settings." + element]: result[element],
        };
    });
}

export function getFormPageStylesChangedValues(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);
    return Object.keys(result).map(element => {
        return {
            ["styles.page." + element]: result[element],
        };
    });
}

export function getFormBackgroundStylesChangedValues(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);
    return Object.keys(result).map(element => {
        return {
            ["styles.background." + element]: result[element],
        };
    });
}

/* Functions for global validation messages actions */

export function getChangedValidationMessageValues(changedItem: MyObject) {
    return Object.keys(changedItem).map(element => {
        return {
            ["validations." + element]: changedItem[element],
        };
    });
}

/*  Functions for payment   */

export function getCompletedPaymentData(data: any) {
    const { _id } = data;
    const resultData: any = mergeObjects(paymentInitial, data || {});
    resultData.paymentID = _id;
    return resultData;
}

export function getChangedProperties(obj1: MyObject, obj2: MyObject): MyObject {
    const result: any = {};

    if (obj1 && obj2) {
        for (const key in obj1) {
            if (!Array.isArray(obj1[key]) && typeof obj1[key] === "object" && obj1[key] !== null && key !== "field") {
                const nested = getChangedProperties(obj1[key], obj2[key]);
                if (Object.keys(nested).length !== 0) {
                    result[key] = nested;
                }
            } else if (obj1[key] !== obj2[key]) {
                result[key] = obj2[key];
            }
        }
        if (Object.keys(result).includes("paymentReceipt")) {
            result["paymentReceipt"] = obj2["paymentReceipt"];
        }
        if (Object.keys(result).includes("payableFields")) {
            result["payableFields"] = obj2["payableFields"];
        }
    }

    return result;
}

//Functions for Form Settings

export function getChangedFormGeneralSettings(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);

    return Object.keys(result).map(element => {
        return {
            ["settings.general." + element]: result[element],
        };
    });
}

export function getChangedFormLimitsSettings(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);

    return Object.keys(result).map(element => {
        return {
            ["settings.limits." + element]: result[element],
        };
    });
}

export function getChangedFormNotificationSettings(currentState: MyObject) {
    const newData = [
        { "settings.notification.emailNotification": currentState.emailNotification },
        { "settings.notification.emailToRespondent": currentState.emailToRespondent },
        { "settings.notification.pushNotification": currentState.pushNotification },
    ];
    return newData;
}

export function getChangedFormSaveProgressSettings(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);

    return Object.keys(result).map(element => {
        return {
            ["settings.saveProgress." + element]: result[element],
        };
    });
}

export function getChangedFormConfirmationSettings(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);

    return Object.keys(result).map(element => {
        return {
            ["settings.confirmation." + element]: result[element],
        };
    });
}

export function getChangedFormNativeSettings(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);

    return Object.keys(result).map(element => {
        return {
            ["settings.native." + element]: result[element],
        };
    });
}

export function getChangedSubmitButtonStylesValues(prevState: MyObject, currentState: MyObject) {
    const result = getObjectWithChangedValues(prevState, currentState);
    return Object.keys(result).map(element => {
        return {
            ["styles.buttons." + element]: result[element],
        };
    });
}
